import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import analyticsService, { ABTestDetail, UpdateABTestPayload } from '../../../services/analyticsService';
import toast from 'react-hot-toast';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const EmailABTestDetail: React.FC = () => {
  const { testId } = useParams<{ testId: string }>();
  const navigate = useNavigate();
  const [testDetails, setTestDetails] = useState<ABTestDetail | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [updating, setUpdating] = useState<boolean>(false);

  useEffect(() => {
    const fetchTestDetails = async () => {
      if (!testId) {
        setError('Test ID is missing');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const data = await analyticsService.getABTestDetails(testId);
        setTestDetails(data);
        setError(null);
      } catch (err: any) {
        setError(err.message || 'Failed to load A/B test details');
        console.error('Error loading A/B test details:', err);
        toast.error('Failed to load A/B test details');
      } finally {
        setLoading(false);
      }
    };

    fetchTestDetails();
  }, [testId]);

  const handleStatusChange = async (status: 'active' | 'completed' | 'cancelled') => {
    if (!testId || !testDetails) return;

    try {
      setUpdating(true);
      const payload: UpdateABTestPayload = { status };
      
      // If completing the test, determine the winner
      if (status === 'completed') {
        // Find the variant with the highest metric based on winnerMetric
        const winnerMetric = testDetails.winner_metric || 'open_rate';
        
        // Sort metrics by the winner metric
        const sortedMetrics = [...testDetails.metrics].sort((a, b) => {
          const bValue = Number(b[winnerMetric as keyof typeof b] || 0);
          const aValue = Number(a[winnerMetric as keyof typeof a] || 0);
          return bValue - aValue;
        });
        
        if (sortedMetrics.length > 0) {
          payload.winnerVariantId = sortedMetrics[0].variant_id;
        }
      }
      
      await analyticsService.updateABTestStatus(testId, payload);
      toast.success(`Test ${status === 'completed' ? 'completed' : status === 'active' ? 'activated' : 'cancelled'} successfully`);
      
      // Refresh data
      const updatedData = await analyticsService.getABTestDetails(testId);
      setTestDetails(updatedData);
    } catch (err: any) {
      console.error('Error updating test status:', err);
      toast.error('Failed to update test status');
    } finally {
      setUpdating(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const prepareChartData = () => {
    if (!testDetails?.metrics) return [];
    
    return testDetails.metrics.map(metric => ({
      name: metric.variant_name,
      'Open Rate': parseFloat((metric.open_rate * 100).toFixed(2)),
      'Click Rate': parseFloat((metric.click_rate * 100).toFixed(2)),
      'Reply Rate': parseFloat((metric.reply_rate * 100).toFixed(2)),
      isControl: metric.is_control
    }));
  };

  const getStatusBadge = (status: string) => {
    switch(status) {
      case 'draft':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">Draft</span>;
      case 'active':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">Active</span>;
      case 'completed':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">Completed</span>;
      case 'cancelled':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">Cancelled</span>;
      default:
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">{status}</span>;
    }
  };

  const getWinnerVariant = () => {
    if (!testDetails || testDetails.status !== 'completed') return null;
    
    const winner = testDetails.variants.find(v => v.id === testDetails.winner_variant_id);
    return winner;
  };

  const renderVariantDetails = () => {
    if (!testDetails?.variants || testDetails.variants.length === 0) return null;
    
    const winnerVariant = getWinnerVariant();
    
    return (
      <div className="mt-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Test Variants</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {testDetails.variants.map(variant => {
            const metrics = testDetails.metrics.find(m => m.variant_id === variant.id);
            const isWinner = winnerVariant && variant.id === winnerVariant.id;
            
            return (
              <div 
                key={variant.id} 
                className={`p-4 border rounded-lg ${isWinner ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-medium text-gray-900">
                      {variant.email_template_variants.name}
                      {variant.email_template_variants.is_control && 
                        <span className="ml-2 text-xs bg-gray-100 text-gray-600 py-0.5 px-2 rounded">Control</span>
                      }
                      {isWinner && 
                        <span className="ml-2 text-xs bg-green-100 text-green-800 py-0.5 px-2 rounded">Winner</span>
                      }
                    </h4>
                    <p className="text-sm text-gray-500 mt-1">Distribution: {variant.distribution_percentage}%</p>
                  </div>
                  {metrics && (
                    <div className="text-right">
                      <p className="text-sm"><span className="font-medium">Sent:</span> {metrics.emails_sent}</p>
                      <p className="text-sm"><span className="font-medium">Open rate:</span> {(metrics.open_rate * 100).toFixed(1)}%</p>
                      <p className="text-sm"><span className="font-medium">Click rate:</span> {(metrics.click_rate * 100).toFixed(1)}%</p>
                    </div>
                  )}
                </div>

                {variant.email_template_variants.variant_type === 'subject' && (
                  <div className="mt-3">
                    <p className="text-xs text-gray-500">Subject Line</p>
                    <p className="text-sm mt-1">{variant.email_template_variants.subject}</p>
                  </div>
                )}

                {variant.email_template_variants.variant_type === 'content' && (
                  <div className="mt-3">
                    <p className="text-xs text-gray-500">Email Content (Preview)</p>
                    <div className="text-sm mt-1 border p-2 rounded max-h-24 overflow-y-auto">
                      {variant.email_template_variants.body.substring(0, 200)}
                      {variant.email_template_variants.body.length > 200 ? '...' : ''}
                    </div>
                  </div>
                )}
                
                {(variant.email_template_variants.variant_type === 'sender_name' || variant.email_template_variants.variant_type === 'send_time') && (
                  <div className="mt-3">
                    <p className="text-xs text-gray-500">{variant.email_template_variants.variant_type === 'sender_name' ? 'Sender Name' : 'Send Time'}</p>
                    <p className="text-sm mt-1">{variant.email_template_variants.body}</p>
                  </div>
                )}

                {metrics && (
                  <div className="mt-3">
                    {metrics.is_control ? (
                      <p className="text-sm text-gray-600">Baseline for comparison</p>
                    ) : (
                      <div>
                        <p className="text-sm">
                          <span className="font-medium">Open rate lift: </span>
                          <span className={metrics.open_rate_lift > 0 ? 'text-green-600' : metrics.open_rate_lift < 0 ? 'text-red-600' : ''}>
                            {metrics.open_rate_lift > 0 ? '+' : ''}{(metrics.open_rate_lift * 100).toFixed(1)}%
                          </span>
                        </p>
                        <p className="text-sm">
                          <span className="font-medium">Click rate lift: </span>
                          <span className={metrics.click_rate_lift > 0 ? 'text-green-600' : metrics.click_rate_lift < 0 ? 'text-red-600' : ''}>
                            {metrics.click_rate_lift > 0 ? '+' : ''}{(metrics.click_rate_lift * 100).toFixed(1)}%
                          </span>
                        </p>
                        <p className="text-sm">
                          <span className="font-medium">Reply rate lift: </span>
                          <span className={metrics.reply_rate_lift > 0 ? 'text-green-600' : metrics.reply_rate_lift < 0 ? 'text-red-600' : ''}>
                            {metrics.reply_rate_lift > 0 ? '+' : ''}{(metrics.reply_rate_lift * 100).toFixed(1)}%
                          </span>
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
        <div>
          <button
            onClick={() => navigate('/email/ab-tests')}
            className="mb-2 flex items-center text-sm text-blue-600 hover:text-blue-800"
          >
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to A/B Tests
          </button>
          <h2 className="text-xl font-semibold text-gray-800">
            {loading ? 'Loading test details...' : testDetails?.name || 'A/B Test Details'}
          </h2>
        </div>
        {testDetails && (
          <div className="flex items-center">
            {getStatusBadge(testDetails.status)}
          </div>
        )}
      </div>

      <div className="px-6 py-4">
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <p className="text-red-500">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Retry
            </button>
          </div>
        ) : testDetails ? (
          <div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Test Information</h3>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-500">Campaign</p>
                        <p className="font-medium">{testDetails.email_campaigns?.name || 'Unknown'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Test Type</p>
                        <p className="font-medium capitalize">{testDetails.test_type?.replace('_', ' ')}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Winner Metric</p>
                        <p className="font-medium capitalize">{testDetails.winner_metric?.replace('_', ' ') || 'Not specified'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Created</p>
                        <p className="font-medium">{formatDate(testDetails.created_at)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Updated</p>
                        <p className="font-medium">{formatDate(testDetails.updated_at)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Status</p>
                        <p className="font-medium capitalize">{testDetails.status}</p>
                      </div>
                    </div>
                    {testDetails.description && (
                      <div className="mt-4">
                        <p className="text-sm text-gray-500">Description</p>
                        <p className="mt-1">{testDetails.description}</p>
                      </div>
                    )}
                  </div>
                </div>
                
                {testDetails.metrics && testDetails.metrics.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Performance Comparison</h3>
                    <div style={{ width: '100%', height: 300 }}>
                      <ResponsiveContainer>
                        <BarChart
                          data={prepareChartData()}
                          margin={{
                            top: 20, right: 30, left: 20, bottom: 5,
                          }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis label={{ value: 'Rate (%)', angle: -90, position: 'insideLeft' }} />
                          <Tooltip formatter={(value) => [`${value}%`, '']} />
                          <Legend />
                          <Bar dataKey="Open Rate" fill="#8884d8" />
                          <Bar dataKey="Click Rate" fill="#82ca9d" />
                          <Bar dataKey="Reply Rate" fill="#ffc658" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}
              </div>
              
              <div>
                {testDetails.status === 'draft' && (
                  <div className="mb-6 bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                    <h3 className="text-md font-medium text-yellow-800 mb-2">Draft Test</h3>
                    <p className="text-sm text-yellow-700 mb-4">
                      This test is currently in draft mode. Activate it to start collecting data and comparing variants.
                    </p>
                    <button
                      onClick={() => handleStatusChange('active')}
                      disabled={updating}
                      className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 disabled:opacity-50"
                    >
                      {updating ? 'Activating...' : 'Activate Test'}
                    </button>
                  </div>
                )}
                
                {testDetails.status === 'active' && (
                  <div className="mb-6 bg-green-50 p-4 rounded-lg border border-green-200">
                    <h3 className="text-md font-medium text-green-800 mb-2">Active Test</h3>
                    <p className="text-sm text-green-700 mb-4">
                      This test is currently active and collecting data. You can complete the test to declare a winner or cancel it.
                    </p>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleStatusChange('completed')}
                        disabled={updating}
                        className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                      >
                        {updating ? 'Processing...' : 'Complete Test & Select Winner'}
                      </button>
                      <button
                        onClick={() => handleStatusChange('cancelled')}
                        disabled={updating}
                        className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                      >
                        {updating ? 'Cancelling...' : 'Cancel Test'}
                      </button>
                    </div>
                  </div>
                )}
                
                {testDetails.status === 'completed' && (
                  <div className="mb-6 bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <h3 className="text-md font-medium text-blue-800 mb-2">Test Completed</h3>
                    <p className="text-sm text-blue-700 mb-2">
                      This test has been completed and a winner has been selected.
                    </p>
                    {getWinnerVariant() && (
                      <div className="mt-2 p-2 bg-green-100 rounded-md">
                        <p className="text-sm font-medium text-green-800">
                          Winner: {getWinnerVariant()?.email_template_variants.name}
                        </p>
                      </div>
                    )}
                  </div>
                )}
                
                {testDetails.status === 'cancelled' && (
                  <div className="mb-6 bg-red-50 p-4 rounded-lg border border-red-200">
                    <h3 className="text-md font-medium text-red-800 mb-2">Test Cancelled</h3>
                    <p className="text-sm text-red-700">
                      This test was cancelled and no winner was selected.
                    </p>
                  </div>
                )}
              </div>
            </div>
            
            {renderVariantDetails()}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default EmailABTestDetail;
